import classNames from "classnames";
import React, { useRef, useState } from "react";
import KuikaPopover from "../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import { ISelectionMenuItem, SelectionMenuKey } from "./selection-menu";

export interface ISelectionMoreMenuProps {
  items: ISelectionMenuItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRun: (key: SelectionMenuKey) => void;
  /** The canvas region: the menu flips/shifts to stay inside it. */
  collisionBoundary: Element | null;
  /** Height cap from `popoverMaxHeight`, so the list scrolls inside the canvas instead of leaving it. */
  maxHeight: number;
  /** The ⋯ trigger. */
  children: React.ReactElement;
}

/** The keys that move focus between the menu's rows (WAI-ARIA menu pattern). */
const ROVING_KEYS: readonly string[] = ["ArrowDown", "ArrowUp", "Home", "End"];

const nextRovingIndex = (key: string, current: number, count: number): number => {
  switch (key) {
    case "ArrowDown":
      return (current + 1) % count;
    case "ArrowUp":
      return (current - 1 + count) % count;
    case "Home":
      return 0;
    default:
      return count - 1;
  }
};

/**
 * The bar's ⋯ menu. A roving-tabindex `menu`: one row is tabbable at a time, Up/Down wrap, Home/End jump,
 * and the popover focuses the first row on open. Escape closes it and focus returns to the ⋯ button —
 * both are the popover's own (Radix) dismiss and focus-restore behaviour.
 */
export const SelectionMoreMenu: React.FC<ISelectionMoreMenuProps> = ({
  items,
  open,
  onOpenChange,
  onRun,
  collisionBoundary,
  maxHeight,
  children
}: ISelectionMoreMenuProps): JSX.Element => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const itemRefs: React.MutableRefObject<(HTMLButtonElement | null)[]> = useRef<(HTMLButtonElement | null)[]>([]);

  // Every opening starts on the first row, whatever row the previous one ended on. The menu only opens
  // from its trigger, which reports through here.
  const handleOpenChange = (next: boolean): void => {
    if (next) {
      setActiveIndex(0);
    }
    onOpenChange(next);
  };

  // A shorter list (the selection changed while open) must still leave one row tabbable.
  const tabbableIndex: number = Math.min(activeIndex, items.length - 1);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (!ROVING_KEYS.includes(event.key) || items.length === 0) {
      return;
    }
    event.preventDefault();
    const current: number = itemRefs.current.findIndex(
      (element: HTMLButtonElement | null): boolean => element !== null && element === document.activeElement
    );
    const next: number = nextRovingIndex(event.key, current === -1 ? tabbableIndex : current, items.length);
    setActiveIndex(next);
    itemRefs.current[next]?.focus();
  };

  return (
    <KuikaPopover
      tone="inverse"
      visible={open}
      onVisibleChange={handleOpenChange}
      position="bottom"
      align="end"
      sideOffset={8}
      collisionPadding={8}
      collisionBoundary={collisionBoundary ?? undefined}
      content={
        <div
          className="kuika-selection-menu kuika-selection-menu--scroll"
          role="menu"
          aria-label="More actions"
          aria-orientation="vertical"
          style={{ maxHeight }}
          onKeyDown={handleKeyDown}
        >
          {items.map((item: ISelectionMenuItem, index: number): JSX.Element => (
            <React.Fragment key={item.key}>
              {item.separatorBefore && <div className="kuika-selection-menu__separator" role="separator" />}
              <button
                ref={(element: HTMLButtonElement | null): void => {
                  itemRefs.current[index] = element;
                }}
                type="button"
                role="menuitem"
                tabIndex={index === tabbableIndex ? 0 : -1}
                className={classNames("kuika-selection-menu__item", {
                  "kuika-selection-menu__item--danger": item.danger
                })}
                data-testid={item.testId}
                onFocus={(): void => setActiveIndex(index)}
                onClick={(): void => {
                  onOpenChange(false);
                  onRun(item.key);
                }}
              >
                <KuikaSvg
                  name={item.icon.name}
                  type={item.icon.type}
                  width={16}
                  height={16}
                  color="currentColor"
                  className="kuika-selection-menu__icon"
                  aria-hidden="true"
                />
                <span className="kuika-selection-menu__label">{item.label}</span>
                {item.shortcut && <kbd className="kuika-selection-menu__kbd">{item.shortcut}</kbd>}
              </button>
            </React.Fragment>
          ))}
        </div>
      }
    >
      {children}
    </KuikaPopover>
  );
};
